/**
 * Plans & Pricing page — single flat-fee panel for the Biz Resilience Scan.
 *
 * Iteration 19: per-tier Stripe pricing is mothballed. Source of truth for the
 * price is `/api/config` (`flat_price_usd`, `payment_processor`, `lunipay_url`).
 * If the admin flips the processor back to Stripe via app_settings, this page
 * automatically restores the legacy 2-card layout via the branch below.
 */
import { Link } from "react-router-dom";
import { useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import TestimonialsSection from "@/components/TestimonialsSection";
import LinkedInBadge from "@/components/LinkedInBadge";
import BookingModal from "@/components/BookingModal";
import { useConfig, clearConfigCache } from "@/lib/config";
import { API } from "@/lib/api";
import { WORKSHOP_TITLE, WORKSHOP_HEADLINE, WORKSHOP_DESC } from "@/lib/constants";
import { Check, ArrowRight, Sparkles, Lock, Calendar, ExternalLink, Loader2, RefreshCw } from "lucide-react";

const MODE_PRETTY = {
  solopreneur: "Solopreneur",
  sme: "Small / Medium Enterprise",
  corporate: "Corporate Department",
};

const fmt = (n) => typeof n === "number" ? `$${n.toFixed(0)}` : "—";

const MODE_BULLETS = {
  solopreneur: [
    "Built for one-operator businesses",
    "Highlights key-person dependency + cash-flow disruption",
    "Personal-recovery framing, not corporate-speak",
  ],
  sme: [
    "Built for teams of 2–250",
    "Supplier concentration, single-site, staff resilience focus",
    "Plan your team can actually execute",
  ],
  corporate: [
    "Built for department leads inside larger organisations",
    "Stakeholder + governance + escalation framing",
    "Outputs ready for your continuity steering forum",
  ],
};

function FlatPricePanel({ price, label, lunipayUrl, paypalUrl, processorName = "Payment Processor", eyebrow, title, copy, bullets = [], ctaLabel, ctaSubtext, mostPopular, footer, extraCta, linkedin }) {
  return (
    <div data-testid={`pricing-card-${(eyebrow || "flat").toLowerCase().replace(/\s+/g, "-")}`} className={`rounded-xl border ${mostPopular ? "border-teal-500 dark:border-teal-400 ring-2 ring-teal-500/20" : "border-slate-200 dark:border-slate-800"} bg-white dark:bg-slate-900 overflow-hidden flex flex-col`}>
      <div className="h-1.5 bg-gradient-to-r from-teal-500 via-teal-600 to-emerald-600" />
      <div className="p-6 sm:p-8 flex flex-col flex-1">
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <p className="text-[11px] uppercase tracking-[0.18em] text-teal-700 dark:text-teal-400">{eyebrow}</p>
          {mostPopular && (
            <span className="text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 rounded-full bg-teal-50 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300 font-medium">
              Most popular
            </span>
          )}
        </div>
        <h3 className="mt-2 font-display text-xl sm:text-2xl font-semibold text-slate-900 dark:text-slate-50 tracking-tight">
          {title}
        </h3>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-300 leading-relaxed lg:min-h-[116px]">
          {copy}
        </p>
        <div className="mt-5 flex items-baseline gap-2">
          <p className="font-display text-4xl font-semibold text-slate-900 dark:text-slate-50">
            {fmt(price)}
          </p>
          <span className="text-xs text-slate-500 dark:text-slate-400">USD · flat fee</span>
        </div>
        <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">{label}</p>
        <ul className="mt-5 space-y-2 text-sm text-slate-700 dark:text-slate-200 lg:min-h-[140px]">
          {bullets.map((b) => (
            <li key={b} className="flex items-start gap-2">
              <Check className="h-4 w-4 text-teal-600 dark:text-teal-400 mt-0.5 shrink-0" /> {b}
            </li>
          ))}
        </ul>
        <div className="mt-6">
          <Link
            to="/mode"
            data-testid={`pricing-${(eyebrow || "flat").toLowerCase().replace(/\s+/g, "-")}-start-cta`}
            className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-slate-900 dark:bg-teal-600 text-white text-sm font-medium hover:bg-slate-800 dark:hover:bg-teal-500 transition-colors w-full justify-center"
          >
            {ctaLabel} <ArrowRight className="h-4 w-4" />
          </Link>
          {lunipayUrl && (
            <a
              href={lunipayUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`pricing-${(eyebrow || "flat").toLowerCase().replace(/\s+/g, "-")}-lunipay-cta`}
              className="mt-3 inline-flex items-center gap-2 h-11 px-5 rounded-md border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-50 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors w-full justify-center"
            >
              Pay {fmt(price)} on {processorName} <ExternalLink className="h-4 w-4" />
            </a>
          )}
          {paypalUrl && (
            <a
              href={paypalUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`pricing-${(eyebrow || "flat").toLowerCase().replace(/\s+/g, "-")}-paypal-cta`}
              className="mt-3 inline-flex items-center gap-2 h-11 px-5 rounded-md border border-[#0070ba] text-[#0070ba] dark:text-sky-300 dark:border-sky-500/60 text-sm font-medium hover:bg-sky-50 dark:hover:bg-sky-500/10 transition-colors w-full justify-center"
            >
              Pay {fmt(price)} with PayPal <ExternalLink className="h-4 w-4" />
            </a>
          )}
          {extraCta && <div className="mt-3">{extraCta}</div>}
          {ctaSubtext && (
            <p className="mt-3 text-[11px] text-slate-500 dark:text-slate-400 text-center">{ctaSubtext}</p>
          )}
        </div>
        {footer && <div className="mt-auto pt-6">{footer}</div>}
        {linkedin?.enabled && linkedin?.url && (
          <div className={`${footer ? "pt-6" : "mt-auto pt-6"}`} data-testid={`pricing-${(eyebrow || "flat").toLowerCase().replace(/\s+/g, "-")}-linkedin`}>
            <LinkedInBadge
              profileUrl={linkedin.url}
              name={linkedin.name}
              title={linkedin.title}
              photoUrl={linkedin.photoUrl}
              vanity={linkedin.vanity}
              label={linkedin.label || "Connect on LinkedIn"}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default function Pricing() {
  const { config, loading } = useConfig();
  const [bookingOpen, setBookingOpen] = useState(false);
  const processorName = config?.processor_name || "Payment Processor";
  const catalog = config?.catalog || [];

  // Curated copy for the two flagship products; any other active catalogue item
  // falls back to its DB display_name + public_description.
  const PRESETS = {
    brief: {
      eyebrow: "Unlock",
      title: "Biz Resilience Scan",
      copy: "One flat fee — same price for Solopreneurs, SMEs, and Corporate teams. Pay once, unlock instantly, keep the clean version forever.",
      bullets: [
        "Clean, watermark-free PDF for download, email, and share",
        "Branded logo on the cover page",
        "30-day shareable link with no DRAFT notice",
        "Saved to your account for re-download anytime",
      ],
      ctaLabel: "Start free scan",
      mostPopular: true,
      extraCta: (
        <div className="h-11 flex items-center justify-center">
          <p className="text-[11px] text-slate-500 dark:text-slate-400 text-center">Take the scan first, then unlock when you're ready.</p>
        </div>
      ),
      footer: (
        <div className="lg:pt-[27px]">
          <div className="rounded-lg overflow-hidden border border-slate-200 dark:border-slate-800 bg-white dark:bg-black shadow-sm lg:min-h-[262px] flex flex-col" data-testid="pricing-scan-resilience-note">
            <div className="px-4 py-2.5 bg-slate-100 dark:bg-[#38434F] border-b border-slate-200 dark:border-[#38434F]">
              <p className="text-sm font-semibold tracking-wide text-teal-700 dark:text-teal-400">Why run a scan?</p>
            </div>
            <div className="px-4 py-4 flex-1">
              <p className="text-sm text-slate-700 dark:text-white leading-relaxed">
                A resilience assessment identifies where your operations are exposed and what matters most during recovery — a thinking tool that helps you understand your dependencies and prioritise actions. The SmartNote2Self Biz Resilience Scan helps business owners make better operational decisions without the jargon, cost, or complexity of traditional continuity planning. The full plan comes later.
              </p>
            </div>
          </div>
        </div>
      ),
    },
    advisory: {
      eyebrow: "Workshop",
      title: WORKSHOP_TITLE,
      copy: `${WORKSHOP_HEADLINE}. ${WORKSHOP_DESC}`,
      bullets: [
        "Live walk-through of your preliminary business resilience scan results with a senior practitioner",
        "Prioritised gap list with concrete next steps",
        "Sharpened one-page operational summary",
        "Schedule any open slot in the next 21 days",
      ],
      ctaLabel: "Start free scan first",
      extraCta: (
        <button
          type="button"
          onClick={() => setBookingOpen(true)}
          data-testid="pricing-workshop-book-cta"
          className="inline-flex items-center gap-2 h-11 px-5 rounded-md border border-teal-600 dark:border-teal-500 text-teal-800 dark:text-teal-200 text-sm font-medium hover:bg-teal-50 dark:hover:bg-teal-900/30 transition-colors w-full justify-center"
        >
          <Calendar className="h-4 w-4" /> Book a time
        </button>
      ),
    },
  };

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 flex flex-col">
      <Header />

      <main className="flex-1 max-w-6xl mx-auto w-full px-6 lg:px-10 py-14">
        <p className="text-[11px] uppercase tracking-[0.18em] text-teal-700 dark:text-teal-400">Plans &amp; pricing</p>
        <h1 className="mt-3 font-display text-3xl sm:text-5xl font-semibold text-slate-900 dark:text-slate-50 tracking-tight max-w-3xl">
          One flat fee.<br />Same value, any size of business.
        </h1>
        <p className="mt-4 text-slate-600 dark:text-slate-300 text-base max-w-2xl leading-relaxed">
          Take the scan for free. Your on-screen brief is always complete — pay only when you want the clean, watermark-free version, or a live 3-hour working session with a senior Biz Resilience practitioner.
        </p>

        {/* Free tier ------------------------------------------------------ */}
        <div className="mt-10 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/40 p-6 sm:p-8" data-testid="pricing-card-free">
          <div className="flex items-baseline justify-between gap-4 flex-wrap">
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Free</p>
              <h3 className="mt-2 font-display text-2xl font-semibold text-slate-900 dark:text-slate-50">Take the scan</h3>
            </div>
            <p className="font-display text-4xl font-semibold text-slate-900 dark:text-slate-50">$0</p>
          </div>
          <ul className="mt-5 grid sm:grid-cols-2 gap-y-2 gap-x-6 text-sm text-slate-700 dark:text-slate-200">
            <li className="flex items-start gap-2"><Check className="h-4 w-4 text-slate-500 mt-0.5 shrink-0" /> 12-question guided assessment in any mode</li>
            <li className="flex items-start gap-2"><Check className="h-4 w-4 text-slate-500 mt-0.5 shrink-0" /> Full brief on screen — no preview limits</li>
            <li className="flex items-start gap-2"><Check className="h-4 w-4 text-slate-500 mt-0.5 shrink-0" /> Risk scenarios, playbooks, and recovery priorities</li>
            <li className="flex items-start gap-2"><Check className="h-4 w-4 text-slate-500 mt-0.5 shrink-0" /> Watermarked PDF + share-link (DRAFT)</li>
          </ul>
          <Link
            to="/mode"
            data-testid="pricing-free-cta"
            className="mt-5 inline-flex items-center gap-2 h-10 px-4 rounded-md border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-50 text-sm font-medium hover:bg-white dark:hover:bg-slate-800 transition-colors"
          >
            Start free assessment <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {/* Flat-fee panels — rendered dynamically from the active catalogue.
            Max 2 columns; wraps to more rows as products are added. */}
        {loading && (
          <div className="mt-10 flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400" data-testid="pricing-catalog-loading">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading plans…
          </div>
        )}
        {!loading && catalog.length === 0 && (
          <div className="mt-10 rounded-lg border border-slate-200 dark:border-slate-800 p-5 flex items-center justify-between gap-4 flex-wrap" data-testid="pricing-catalog-unavailable">
            <p className="text-sm text-slate-600 dark:text-slate-300">We couldn't load our paid plans just now.</p>
            <button type="button" onClick={() => { clearConfigCache(); window.location.reload(); }} data-testid="pricing-catalog-retry"
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-teal-600 text-white text-sm font-medium hover:bg-teal-500 transition-colors">
              <RefreshCw className="h-4 w-4" /> Try again
            </button>
          </div>
        )}
        <div className="mt-10 grid gap-6 md:grid-cols-2" data-testid="pricing-catalog-grid">
          {catalog.map((item) => {
            const preset = PRESETS[item.slug] || {};
            return (
              <FlatPricePanel
                key={item.slug}
                eyebrow={preset.eyebrow || item.display_name}
                title={preset.title || item.display_name}
                copy={preset.copy || item.public_description || ""}
                price={item.price}
                label={item.price_label}
                lunipayUrl={item.lunipay_url}
                paypalUrl={item.paypal_url}
                processorName={processorName}
                bullets={item.features?.length ? item.features : (preset.bullets || [])}
                ctaLabel={preset.ctaLabel || "Start free scan"}
                mostPopular={preset.mostPopular}
                extraCta={preset.extraCta}
                footer={preset.footer}
                linkedin={{
                  enabled: item.linkedin_enabled,
                  url: item.linkedin_url,
                  vanity: item.linkedin_vanity,
                  name: item.linkedin_name,
                  title: item.linkedin_title,
                  photoUrl: item.linkedin_photo_url || (item.linkedin_photo_path ? `${API}/blogs/media/${item.linkedin_photo_path}` : ""),
                  label: item.linkedin_label,
                }}
              />
            );
          })}
        </div>

        {/* Three tier-experience cards (no prices, just experience differentiation) */}
        <div className="mt-12">
          <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Same price · different experience</p>
          <h2 className="mt-2 font-display text-2xl font-semibold text-slate-900 dark:text-slate-50">
            Three experiences. One unlock.
          </h2>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-300 max-w-2xl">
            The scan adapts to how you actually operate — same flat fee, the experience is tailored to your scale.
          </p>
          <div className="mt-6 grid md:grid-cols-3 gap-4">
            {["solopreneur", "sme", "corporate"].map((mode) => (
              <div
                key={mode}
                data-testid={`pricing-tier-card-${mode}`}
                className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5"
              >
                <p className="text-[10px] uppercase tracking-[0.14em] text-teal-700 dark:text-teal-400">{MODE_PRETTY[mode]}</p>
                <ul className="mt-3 space-y-2 text-sm text-slate-700 dark:text-slate-200">
                  {MODE_BULLETS[mode].map((b) => (
                    <li key={b} className="flex items-start gap-2">
                      <Check className="h-3.5 w-3.5 text-teal-600 dark:text-teal-400 mt-1 shrink-0" /> {b}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Reassurance + secondary CTA ----------------------------------- */}
        <div className="mt-12 rounded-xl border border-slate-200 dark:border-slate-800 p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <Sparkles className="h-5 w-5 text-teal-600 dark:text-teal-400 mt-0.5" />
            <div>
              <p className="font-display text-base font-semibold text-slate-900 dark:text-slate-50">Not sure?</p>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">Start the free scan first. You can unlock the clean version at any time from the summary page.</p>
            </div>
          </div>
          <Link
            to="/mode"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-slate-900 dark:bg-teal-600 text-white text-sm font-medium hover:bg-slate-800 dark:hover:bg-teal-500 transition-colors"
          >
            Start free <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {/* FAQ / fine print ---------------------------------------------- */}
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          <div>
            <div className="flex items-center gap-2 text-slate-900 dark:text-slate-50">
              <Lock className="h-4 w-4 text-teal-600 dark:text-teal-400" />
              <p className="font-medium">Secure payment</p>
            </div>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">Payment processed securely by {processorName} or PayPal. We never see or store card details.</p>
          </div>
          <div>
            <div className="flex items-center gap-2 text-slate-900 dark:text-slate-50">
              <Calendar className="h-4 w-4 text-teal-600 dark:text-teal-400" />
              <p className="font-medium">One-off, not subscription</p>
            </div>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">Pay per scan. No recurring charges.</p>
          </div>
          <div>
            <div className="flex items-center gap-2 text-slate-900 dark:text-slate-50">
              <Check className="h-4 w-4 text-teal-600 dark:text-teal-400" />
              <p className="font-medium">Pricing currency</p>
            </div>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">Price is in US Dollars. Your bank may show a small FX conversion if you pay in a different currency.</p>
          </div>
        </div>

        {loading && (
          <p className="mt-8 text-xs text-slate-400 dark:text-slate-500">Loading current price…</p>
        )}
      </main>

      <div className="px-6 lg:px-10 pb-8 pt-4">
        <TestimonialsSection surface="pricing" bare heading="Operators who ran the scan" />
      </div>
      <div className="px-6 lg:px-10 pb-20 pt-4 max-w-6xl mx-auto w-full">
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-testid="pricing-learn-more-cta">
          <div>
            <p className="font-display text-base font-semibold text-slate-900 dark:text-slate-50">Have questions?</p>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">Read our FAQ and resilience guides on the Learn more page.</p>
          </div>
          <Link
            to="/learn-more"
            data-testid="pricing-learn-more-link"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-50 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors whitespace-nowrap"
          >
            Visit Learn more <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      <BookingModal
        open={bookingOpen}
        onClose={() => setBookingOpen(false)}
        assessmentId={null}
      />

      <Footer />
    </div>
  );
}
